import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  UserCredential,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  AuthError,
  AuthErrorCodes,
} from "firebase/auth";
import { logger } from "../utils/logger";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  DocumentData,
  WithFieldValue,
} from "firebase/firestore";
import { Credentials } from "../types/auth";
import { getConfig } from "../config/environment";

const firebaseConfig = getConfig().firebase;

logger.debug("[Firebase] Initializing with config:", {
  apiKey: firebaseConfig.apiKey ? "****" : undefined,
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
});

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Configure auth persistence with local storage
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    logger.debug("[Firebase] Auth persistence set to LOCAL");
  })
  .catch((error) => {
    logger.error("[Firebase] Error setting auth persistence:", error);
  });

// Constants for auth expiration
const AUTH_EXPIRATION_DAYS = 14;
const AUTH_LAST_LOGIN_KEY = "auth_last_login";

// Helper to check if auth has expired
const hasAuthExpired = (): boolean => {
  const lastLogin = localStorage.getItem(AUTH_LAST_LOGIN_KEY);
  // Don't expire if no lastLogin - this means it's a fresh login
  if (!lastLogin) return false;

  const lastLoginDate = new Date(lastLogin);
  const now = new Date();
  const diffDays =
    (now.getTime() - lastLoginDate.getTime()) / (1000 * 60 * 60 * 24);

  return diffDays > AUTH_EXPIRATION_DAYS;
};

// Helper to update last login time
const updateLastLoginTime = () => {
  localStorage.setItem(AUTH_LAST_LOGIN_KEY, new Date().toISOString());
};

// Helper to handle Firebase auth errors
const handleAuthError = (error: AuthError): Error => {
  let message = "Something went wrong. Please try again or contact support.";

  // Log all auth errors with code and message for debugging
  logger.error("[Firebase] Auth error details:", {
    code: error.code,
    message: error.message,
    stack: new Error().stack,
  });

  switch (error.code) {
    case AuthErrorCodes.INVALID_PASSWORD:
    case AuthErrorCodes.USER_DELETED:
    case "auth/invalid-credential":
      message =
        "The email or password you entered is incorrect. Please try again.";
      break;
    case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
      message = "Too many failed attempts. Please try again later.";
      break;
    case AuthErrorCodes.EMAIL_EXISTS:
      message =
        "Email address already in use. Please use a different email or sign in.";
      break;
    case AuthErrorCodes.POPUP_CLOSED_BY_USER:
      message = "Sign in with Google was cancelled. Please try again.";
      break;
    case AuthErrorCodes.NETWORK_REQUEST_FAILED:
      message =
        "Network error. Please check your internet connection and try again.";
      break;
    case AuthErrorCodes.EXPIRED_POPUP_REQUEST:
      message = "The sign in attempt timed out. Please try again.";
      break;
    default:
      logger.error("[Firebase] Unhandled auth error code:", error.code);
  }

  return new Error(message);
};

// Log Firebase initialization
logger.info("Firebase initialized", {
  projectId: app.options.projectId,
  databaseURL: app.options.databaseURL,
  timestamp: new Date().toISOString(),
});

interface FirebaseAuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

const googleProvider = new GoogleAuthProvider();

export const firebaseService = {
  auth: {
    signInWithGoogle: async (): Promise<UserCredential> => {
      try {
        const result = await signInWithPopup(auth, googleProvider);
        updateLastLoginTime();
        return result;
      } catch (error) {
        throw handleAuthError(error as AuthError);
      }
    },
    signIn: async (credentials: Credentials): Promise<UserCredential> => {
      try {
        const result = await signInWithEmailAndPassword(
          auth,
          credentials.email,
          credentials.password
        );
        updateLastLoginTime();
        return result;
      } catch (error) {
        throw handleAuthError(error as AuthError);
      }
    },
    signUp: async (credentials: Credentials): Promise<UserCredential> => {
      const TIMEOUT = 10000; // 10 second timeout
      const MAX_RETRIES = 3;
      let retryCount = 0;

      // Helper to verify auth record creation
      const verifyAuthRecord = async (userCredential: UserCredential) => {
        try {
          // Add a small delay to allow Firebase to complete its operations
          await new Promise((resolve) => setTimeout(resolve, 500));

          // First check current user
          const currentUser = auth.currentUser;
          if (!currentUser || currentUser.uid !== userCredential.user.uid) {
            logger.error("[Firebase] Auth record verification failed", {
              expectedUid: userCredential.user.uid,
              currentUid: currentUser?.uid,
              stack: new Error().stack,
            });
            // Log error but don't throw to allow registration to continue
            logger.warn("[Firebase] Continuing despite verification failure");
            return;
          }

          // Then verify through getCurrentUser
          const verifiedUser = await new Promise<FirebaseAuthUser | null>(
            (resolve, reject) => {
              const unsubscribe = onAuthStateChanged(
                auth,
                (user) => {
                  unsubscribe();
                  if (user) {
                    resolve({
                      uid: user.uid,
                      email: user.email,
                      displayName: user.displayName,
                    });
                  } else {
                    resolve(null);
                  }
                },
                reject
              );
            }
          );

          if (!verifiedUser || verifiedUser.uid !== userCredential.user.uid) {
            logger.error(
              "[Firebase] Auth record persistence verification failed",
              {
                expectedUid: userCredential.user.uid,
                verifiedUid: verifiedUser?.uid,
                stack: new Error().stack,
              }
            );
            // Log error but don't throw to allow registration to continue
            logger.warn(
              "[Firebase] Continuing despite persistence verification failure"
            );
            return;
          }

          logger.debug("[Firebase] Auth record verified and persisted", {
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            stack: new Error().stack,
          });
        } catch (error) {
          // Log any errors but don't throw to allow registration to continue
          logger.error("[Firebase] Error during auth verification", {
            error,
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            stack: new Error().stack,
          });
          logger.warn("[Firebase] Continuing despite verification error");
        }
      };

      // Helper to update user profile
      const updateUserProfile = async (userCredential: UserCredential) => {
        if (credentials.firstName && credentials.lastName) {
          const displayName = `${credentials.firstName} ${credentials.lastName}`;
          const maxRetries = 3;
          let profileRetryCount = 0;

          while (profileRetryCount < maxRetries) {
            try {
              await updateProfile(userCredential.user, { displayName });
              logger.debug("[Firebase] User profile updated successfully", {
                uid: userCredential.user.uid,
                displayName: userCredential.user.displayName,
              });
              break;
            } catch (updateError) {
              profileRetryCount++;
              if (profileRetryCount === maxRetries) throw updateError;
              await new Promise((resolve) =>
                setTimeout(resolve, Math.pow(2, profileRetryCount) * 1000)
              );
            }
          }
        }
      };

      while (retryCount < MAX_RETRIES) {
        try {
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error("Sign up timed out")), TIMEOUT);
          });

          const signUpPromise = createUserWithEmailAndPassword(
            auth,
            credentials.email,
            credentials.password
          );

          const userCredential = (await Promise.race([
            signUpPromise,
            timeoutPromise,
          ])) as UserCredential;

          logger.debug("[Firebase] Auth record created", {
            uid: userCredential.user.uid,
            email: userCredential.user.email,
          });

          // Verify the auth record
          await verifyAuthRecord(userCredential);

          // Update the user profile
          try {
            await updateUserProfile(userCredential);
          } catch (profileError) {
            logger.error(
              "[Firebase] Error updating user profile:",
              profileError
            );
            // Continue even if profile update fails
          }

          return userCredential;
        } catch (error) {
          retryCount++;
          if (error instanceof Error && error.message === "Sign up timed out") {
            if (retryCount === MAX_RETRIES) {
              throw new Error(
                "Sign up process timed out after multiple attempts"
              );
            }
            logger.warn("[Firebase] Sign up attempt timed out, retrying...");
            continue;
          }

          if (retryCount === MAX_RETRIES) {
            throw handleAuthError(error as AuthError);
          }

          await new Promise((resolve) =>
            setTimeout(resolve, Math.pow(2, retryCount) * 1000)
          );
        }
      }

      throw new Error("Sign up failed after maximum retries");
    },
    signOut: async () => {
      // Clear our session-expiry marker; auth/registration state is cleared by
      // the auth store's logout action.
      localStorage.removeItem(AUTH_LAST_LOGIN_KEY);
      await firebaseSignOut(auth);
    },
    getCurrentUser: () => {
      return new Promise<FirebaseAuthUser | null>((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(
          auth,
          (user) => {
            unsubscribe();
            if (user) {
              resolve({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
              });
            } else {
              resolve(null);
            }
          },
          reject
        );
      });
    },
    onAuthStateChanged: (callback: (user: FirebaseAuthUser | null) => void) => {
      let lastUserId: string | null = null;

      return onAuthStateChanged(auth, async (user) => {
        // Check for auth expiration
        if (user && hasAuthExpired()) {
          logger.debug("[Firebase] Auth expired, signing out user");
          localStorage.removeItem(AUTH_LAST_LOGIN_KEY);
          await firebaseSignOut(auth);
          return;
        }
        logger.debug("[Firebase] Auth state changed", {
          hasUser: !!user,
          userId: user?.uid,
          email: user?.email,
          displayName: user?.displayName,
          lastUserId,
          stack: new Error().stack,
        });

        // Handle no user case
        if (!user) {
          if (lastUserId) {
            logger.debug("[Firebase] User signed out", {
              previousUserId: lastUserId,
              stack: new Error().stack,
            });
          } else {
            logger.debug("[Firebase] Initial auth check - no user", {
              stack: new Error().stack,
            });
          }
          lastUserId = null;
          callback(null); // Always call callback for no user
          return;
        }

        // For sign in/up, only call if user ID changed
        if (user.uid !== lastUserId) {
          logger.debug("[Firebase] New user signed in", {
            userId: user.uid,
            email: user.email,
            displayName: user.displayName,
            previousUserId: lastUserId,
            stack: new Error().stack,
          });
          lastUserId = user.uid;
          callback({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
          });
        }
      });
    },
  },
  firestore: {
    getDoc: async <T>(path: string): Promise<T> => {
      const docRef = doc(db, path);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error("Document not found");
      }
      return docSnap.data() as T;
    },
    setDoc: async (path: string, data: unknown) => {
      const docRef = doc(db, path);
      await setDoc(docRef, data as WithFieldValue<DocumentData>);
    },
    updateDoc: async (path: string, data: unknown) => {
      const docRef = doc(db, path);
      await updateDoc(docRef, data as WithFieldValue<DocumentData>);
    },
    deleteDoc: async (path: string) => {
      const docRef = doc(db, path);
      await deleteDoc(docRef);
    },
  },
};
